import type { TrainingSession, TrainingWeek } from '@cadencia/contracts';
import { assessWeekClose, weekIsFinished } from '@cadencia/domain';
import { addDays, todayLocal } from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import {
  ensureWeekMaterialized,
  getActiveBlock,
  listSessionsOfWeek,
  listWeeks,
  updateSession,
  type EnsureWeekResult,
} from '../repositories/trainingRepo.js';
import type { WriteContext } from '../writes.js';
import { evaluateDueDecisions } from './outcomeService.js';
import { runWeeklyReview } from './weeklyReviewService.js';

/**
 * Dar por terminada la semana, y preparar la siguiente (decisión del usuario,
 * 13/09/2026; ver `domain/training/week-close.ts`).
 *
 * EL ORDEN IMPORTA
 *
 * 1. Las sesiones que no se van a hacer pasan a `skipped`. La reorganización
 *    del §8 las ignora —no se mueven a otro día, que es lo que se pide— y la
 *    revisión semanal las cuenta como no hechas, igual que una perdida.
 * 2. **La revisión de ESA semana corre antes de preparar la siguiente.** Los
 *    cambios menores se aplican solos en ella, así que entran en la semana
 *    nueva. Los relevantes esperan a que se aprueben, y si se aprueban antes de
 *    entrenar la semana preparada, `applyDecisions` la rehace
 *    (`semanaPreparada.ts`).
 * 3. Se materializa la semana siguiente, que pasa a ser la activa.
 */

export interface CloseWeekResult {
  skipped: TrainingSession[];
  /** `null` cuando la semana cerrada era la última del bloque. */
  nextWeek: EnsureWeekResult | null;
  reasonCodes: string[];
}

/** La semana del bloque activo que contiene `today`. */
async function semanaDeHoy(
  userId: string,
  today: string,
): Promise<{ week: TrainingWeek; weeks: TrainingWeek[] } | null> {
  const block = await getActiveBlock(userId);
  if (!block) return null;
  const weeks = await listWeeks(block.id);
  const week = weeks.find((fila) => fila.weekStart <= today && today < addDays(fila.weekStart, 7));
  return week === undefined ? null : { week, weeks };
}

export async function closeWeek(ctx: WriteContext, today = todayLocal()): Promise<CloseWeekResult> {
  const actual = await semanaDeHoy(ctx.userId, today);
  if (actual === null) return { skipped: [], nextWeek: null, reasonCodes: ['no_current_week'] };

  const { closable, inProgress } = assessWeekClose(await listSessionsOfWeek(actual.week.id));
  if (inProgress.length > 0) {
    return { skipped: [], nextWeek: null, reasonCodes: ['session_in_progress'] };
  }

  const skipped: TrainingSession[] = [];
  for (const sesion of closable) {
    skipped.push(await updateSession(ctx, sesion, { status: 'skipped' }));
  }

  // Antes de la revisión: lo que se aplicó hace unas semanas se juzga con la
  // semana que acaba de cerrarse dentro (E5).
  await evaluateDueDecisions(ctx, today);
  await runWeeklyReview(ctx, { weekStart: actual.week.weekStart, today, recordKeptPlan: true });
  const nextWeek = await ensureWeekMaterialized(ctx, addDays(actual.week.weekStart, 7));

  logger.info('engine', 'WEEK_CLOSED', 'Semana dada por terminada', {
    weekIndex: actual.week.weekIndex,
    saltadas: skipped.length,
    siguiente: nextWeek?.weekIndex ?? null,
  });

  return {
    skipped,
    nextWeek,
    reasonCodes: nextWeek === null ? ['week_closed', 'block_ends'] : ['week_closed'],
  };
}

/**
 * Prepara la semana siguiente sola cuando la de hoy ya no tiene nada
 * pendiente: todo hecho, perdido o dado por no hecho.
 *
 * Es el caso del domingo con la semana completada: no hay nada que cerrar, y
 * obligar a pulsar un botón para ver el lunes sería burocracia.
 */
export async function prepareNextWeekIfFinished(
  ctx: WriteContext,
  today = todayLocal(),
): Promise<EnsureWeekResult | null> {
  const actual = await semanaDeHoy(ctx.userId, today);
  if (actual === null) return null;

  const yaPreparada = actual.weeks.some(
    (fila) => fila.status === 'active' && fila.weekIndex > actual.week.weekIndex,
  );
  if (yaPreparada) return null;

  if (!weekIsFinished(await listSessionsOfWeek(actual.week.id))) return null;

  // Antes de la revisión: lo que se aplicó hace unas semanas se juzga con la
  // semana que acaba de cerrarse dentro (E5).
  await evaluateDueDecisions(ctx, today);
  await runWeeklyReview(ctx, { weekStart: actual.week.weekStart, today, recordKeptPlan: true });
  return ensureWeekMaterialized(ctx, addDays(actual.week.weekStart, 7));
}
